import {
  ENDPOINTS,
  RequestPipelineRunRequestSchema,
  RunIdRequestSchema,
  SetPipelineScheduleRequestSchema,
  type ApprovePipelineRunResponse,
  type CancelPipelineRunResponse,
  type GetPipelineRunResponse,
  type GetPipelineScheduleResponse,
  type ListPipelineRunsResponse,
  type PipelineRunFilter,
  type RequestPipelineRunResponse,
  type RunPipelineNowResponse,
  type SetPipelineScheduleRequest,
  type SetPipelineScheduleResponse,
} from '@clm/shared-types';

import { apiGet, apiPost } from './client';
import { substitutePath } from './verifications';

/**
 * The eight pipeline-run calls behind the Pipeline page's **Runs** section —
 * CLM-83 (R-83-1; D-83-2 … D-83-15).
 *
 * Same recipe as `pipeline-status.ts` / `pipeline-rerun.ts`: the path comes
 * from the registry, every request is `.parse`d before it leaves the browser
 * (the schemas are `.strict()`), and every response is **parsed** against the
 * registry schema so an off-contract answer is a defect to report, not data to
 * show.
 *
 * Runs are org-level (D-83-14) — no scope key is ever encoded here; the action
 * check is the API's. A 409 on request / run-now carries
 * `run_already_pending` in `field_errors[0].message` (one queue slot, D-83-5);
 * a 403 on approve / run-now / schedule means the caller is not an admin
 * (D-83-2, D-83-3). None of these calls starts the run itself — the API-side
 * worker claims an approved row within its poll interval.
 */

const REQUEST = ENDPOINTS['request-pipeline-run'];
const RUN_NOW = ENDPOINTS['run-pipeline-now'];
const APPROVE = ENDPOINTS['approve-pipeline-run'];
const CANCEL = ENDPOINTS['cancel-pipeline-run'];
const LIST = ENDPOINTS['list-pipeline-runs'];
const GET = ENDPOINTS['get-pipeline-run'];
const GET_SCHEDULE = ENDPOINTS['get-pipeline-schedule'];
const SET_SCHEDULE = ENDPOINTS['set-pipeline-schedule'];

/** What the form hands over: the schema fills `filter.limit` (25) and an absent `filter`. */
export interface RequestPipelineRunInput {
  readonly reason: string;
  readonly filter?: Partial<PipelineRunFilter> | undefined;
}

function options(signal: AbortSignal | undefined): { readonly signal: AbortSignal } | undefined {
  return signal === undefined ? undefined : { signal };
}

/** `POST /v1/pipeline/runs` — a contributor's request; `status: requested` until an admin approves. */
export async function requestPipelineRun(
  request: RequestPipelineRunInput,
  signal?: AbortSignal,
): Promise<RequestPipelineRunResponse> {
  const validated = RequestPipelineRunRequestSchema.parse(request);
  const raw = await apiPost<unknown>(REQUEST.path, validated, options(signal));
  return REQUEST.response.parse(raw);
}

/** `POST /v1/pipeline/runs/now` — admin only: create and approve in one action (D-83-3). */
export async function runPipelineNow(
  request: RequestPipelineRunInput,
  signal?: AbortSignal,
): Promise<RunPipelineNowResponse> {
  const validated = RequestPipelineRunRequestSchema.parse(request);
  const raw = await apiPost<unknown>(RUN_NOW.path, validated, options(signal));
  return RUN_NOW.response.parse(raw);
}

/** `POST /v1/pipeline/runs/{id}/approve` — admin only (D-83-2). Empty body; the id rides in the path. */
export async function approvePipelineRun(pipelineRunId: string, signal?: AbortSignal): Promise<ApprovePipelineRunResponse> {
  const { pipeline_run_id } = RunIdRequestSchema.parse({ pipeline_run_id: pipelineRunId });
  const raw = await apiPost<unknown>(substitutePath(APPROVE.path, { pipeline_run_id }), {}, options(signal));
  return APPROVE.response.parse(raw);
}

/** `POST /v1/pipeline/runs/{id}/cancel` — own requested row, or any active row for an admin (D-83-7). */
export async function cancelPipelineRun(pipelineRunId: string, signal?: AbortSignal): Promise<CancelPipelineRunResponse> {
  const { pipeline_run_id } = RunIdRequestSchema.parse({ pipeline_run_id: pipelineRunId });
  const raw = await apiPost<unknown>(substitutePath(CANCEL.path, { pipeline_run_id }), {}, options(signal));
  return CANCEL.response.parse(raw);
}

/** `GET /v1/pipeline/runs` — active, pending and the last twenty terminal runs. */
export async function listPipelineRuns(signal?: AbortSignal): Promise<ListPipelineRunsResponse> {
  const raw = await apiGet<unknown>(LIST.path, options(signal));
  return LIST.response.parse(raw);
}

/** `GET /v1/pipeline/runs/{id}` — one row; the active card polls it every 5 s. */
export async function getPipelineRun(pipelineRunId: string, signal?: AbortSignal): Promise<GetPipelineRunResponse> {
  const { pipeline_run_id } = RunIdRequestSchema.parse({ pipeline_run_id: pipelineRunId });
  const raw = await apiGet<unknown>(substitutePath(GET.path, { pipeline_run_id }), options(signal));
  return GET.response.parse(raw);
}

/** `GET /v1/pipeline/schedule` — admin only; the singleton. */
export async function getPipelineSchedule(signal?: AbortSignal): Promise<GetPipelineScheduleResponse> {
  const raw = await apiGet<unknown>(GET_SCHEDULE.path, options(signal));
  return GET_SCHEDULE.response.parse(raw);
}

/** `POST /v1/pipeline/schedule` — admin only; `{ enabled, hour_utc, batch_limit }` and nothing else (D-83-4). */
export async function setPipelineSchedule(
  request: SetPipelineScheduleRequest,
  signal?: AbortSignal,
): Promise<SetPipelineScheduleResponse> {
  const validated = SetPipelineScheduleRequestSchema.parse(request);
  const raw = await apiPost<unknown>(SET_SCHEDULE.path, validated, options(signal));
  return SET_SCHEDULE.response.parse(raw);
}
